"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { HeartIcon } from "lucide-react";

import Badge from "@components/Badge/Badge";
import Link from "@components/Link/Link";
import { Button } from "@components/ngg";
import { useAuth } from "@context/authContext";
import { useCartActions, useCartState } from "@context/cartContext";
import { useHeaderContext } from "@context/headerContext";
import { useSiteInfo } from "@context/siteInfoContext";
import type { ReactNode } from "react";

import { env } from "@/env";
import useEvents from "@app/_hooks/useEvents";
import { useWishlist } from "@app/_hooks/useWishlist";
import CartIcon from "@app/_icons/CartIcon/CartIcon";
import UserIcon from "@app/_icons/UserIcon/UserIcon";

export const LoginButton = ({
  locale,
  className,
  children,
  redirectOnSuccess = true,
}: {
  locale?: string;
  className?: string;
  children: ReactNode;
  redirectOnSuccess?: boolean;
}) => {
  const t = useTranslate();
  const { myPagesStartPage } = useSiteInfo();
  const { auth, showLogin } = useAuth();

  return auth?.user ? (
    <Button
      as={Link}
      locale={locale}
      href={myPagesStartPage?.slug}
      className={className}
      title={t("myPages")}
      aria-label={t("myPages")}
    >
      {children}
    </Button>
  ) : (
    <Button
      onClick={() => showLogin(redirectOnSuccess)}
      type="button"
      className={className}
      title={t("login")}
      aria-label={t("login")}
    >
      {children}
    </Button>
  );
};

const Actions = () => {
  const t = useTranslate();
  const { viewCart } = useEvents();
  const { locale } = useHeaderContext();
  const { wishlist } = useSiteInfo();
  const { cart } = useCartState();
  const { showCart } = useCartActions();
  const { total } = useWishlist();

  const numItemsInCart = cart?.items
    ?.filter(
      (item) =>
        item?.partNo !== `${env.NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO}`,
    )
    ?.reduce((total, item) => total + (item?.quantity || 0), 0);

  const handleOnClick = () => {
    showCart();
    if (cart) viewCart(cart);
  };

  return (
    <div className="flex items-center gap-1">
      <LoginButton
        locale={locale}
        className="hidden items-center p-1 md:flex [&>span]:hidden"
      >
        <UserIcon className="size-4" />
      </LoginButton>
      <Link
        className="p-1"
        locale={locale}
        href={wishlist?.slug}
        title={t("wishlist")}
        aria-label={t("wishlist")}
      >
        <Badge badgeContent={total}>
          <HeartIcon strokeWidth={1} className="size-6" />
        </Badge>
      </Link>
      <button
        className="p-1"
        type="button"
        onClick={handleOnClick}
        data-testid="cart-icon-button"
        title={t("yourCart")}
        aria-label={t("yourCart")}
      >
        <Badge badgeContent={numItemsInCart}>
          <CartIcon className="size-5" />
        </Badge>
      </button>
    </div>
  );
};

export default Actions;
